import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AppLayout } from './components/Layout/AppLayout';
import { ProtectedRoute } from './components/ProtectedRoute/ProtectedRoute';
import { AuthLayout } from './components/Layout/AuthLayout';
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { RootDashboardPage } from './pages/RootDashboardPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { AddProjectPage } from './pages/AddProjectPage';
import { EditProjectPage } from './pages/EditProjectPage';
import { AreasManagementPage } from './pages/AreasManagementPage';
import { EquipmentsManagementPage } from './pages/EquipmentsManagementPage';
import { UsersManagementPage } from './pages/UsersManagementPage';
import { ProjectMembersPage } from './pages/ProjectMembersPage';
import { ProjectAccountsPage } from './pages/BoxAccountsPage';
import { AlarmsManagementPage } from './pages/AlarmsManagementPage';
import { AttributeDataPage } from './pages/AttributeDataPage';
import { AdminPartiesCommunesDevicesPage } from './pages/AdminPartiesCommunesDevicesPage';
import { ProjectImagePage } from './pages/ProjectImagePage';
import { ProjectAnalysesPage } from './pages/ProjectAnalysesPage';
import { ProjectAnalysesConsommationPage } from './pages/ProjectAnalysesConsommationPage';
import { ProjectAnalysesPuissancePage } from './pages/ProjectAnalysesPuissancePage';
import { ProjectAnalysesConfortPage } from './pages/ProjectAnalysesConfortPage';
import { ProjectAnalysesComparaisonsPage } from './pages/ProjectAnalysesComparaisonsPage';
import { ProjectAnalysesRapportsPage } from './pages/ProjectAnalysesRapportsPage';
import { ProjectAnalysesLogementPage } from './pages/ProjectAnalysesLogementPage';
import { LogoutPage } from './pages/LogoutPage';
import { ImportAreasPage } from './pages/ImportAreasPage';
import { ImportBoxAssociationPage } from './pages/ImportBoxAssociationPage';
import { UserDevicesPage } from './pages/UserDevicesPage';
import { CartePage } from './pages/CartePage';
import { LogementsPage } from './pages/LogementsPage';
import { PlanningPage } from './pages/PlanningPage';
import { PartiesCommunesPage } from './pages/PartiesCommunesPage';
import { DefautsPage } from './pages/DefautsPage';
import { DocumentationsPage } from './pages/DocumentationsPage';
import { ProfilePage } from './pages/ProfilePage';
import { NavigateToSelectedProject } from './components/NavigateToSelectedProject';
import { AlertsConfigPage } from './pages/Alerts/AlertsConfigPage';
import { InfluxApiTestPage } from './pages/InfluxApiTestPage';
import { MetricCatalogPage } from './pages/MetricCatalogPage';
import { SmaltCoreIngestSyncPage } from './pages/admin/SmaltCoreIngestSyncPage';
import { SuperTopologyDebugPage } from './pages/admin/SuperTopologyDebugPage';
import { SuperZipatoAnalysisPage } from './pages/admin/SuperZipatoAnalysisPage';
import { SuperAssociationsPage } from './pages/admin/SuperAssociationsPage';
import { SuperCheckDeviceExistPage } from './pages/admin/SuperCheckDeviceExistPage';
import { SuperImportEquipmentsPage } from './pages/admin/SuperImportEquipmentsPage';
import { ProjectAssociationsPage } from './pages/admin/ProjectAssociationsPage';
import { AdminIndexRedirect } from './pages/admin/AdminIndexRedirect';
import { useTokenRefresh } from './hooks/useTokenRefresh';

function App() {
  // Initialize proactive token refresh
  useTokenRefresh();

  return (
    <Routes>
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>
      <Route path="/logout" element={<LogoutPage />} />
      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/dashboard/profile" element={<ProfilePage />} />
        <Route path="/dashboard/:projectId" element={<DashboardPage />} />

        {/* Project-scoped user routes */}
        <Route path="/dashboard/:projectId/boxs" element={<UserDevicesPage />} />
        <Route path="/dashboard/:projectId/carte" element={<CartePage />} />
        <Route path="/dashboard/:projectId/logements" element={<LogementsPage />} />
        <Route path="/dashboard/:projectId/planning" element={<PlanningPage />} />
        <Route path="/dashboard/:projectId/parties-communes" element={<PartiesCommunesPage />} />
        <Route path="/dashboard/:projectId/defauts" element={<DefautsPage />} />
        <Route path="/dashboard/:projectId/documentations" element={<DocumentationsPage />} />

        {/* Backward-compat routes: redirect to selected project if any */}
        <Route path="/dashboard/boxs" element={<NavigateToSelectedProject to="boxs" />} />
        <Route path="/dashboard/carte" element={<NavigateToSelectedProject to="carte" />} />
        <Route path="/dashboard/logements" element={<NavigateToSelectedProject to="logements" />} />
        <Route path="/dashboard/planning" element={<NavigateToSelectedProject to="planning" />} />
        <Route path="/dashboard/parties-communes" element={<NavigateToSelectedProject to="parties-communes" />} />
        <Route path="/dashboard/defauts" element={<NavigateToSelectedProject to="defauts" />} />
        <Route path="/dashboard/documentations" element={<NavigateToSelectedProject to="documentations" />} />
        <Route path="/admin" element={<Outlet />}>
          <Route index element={<AdminIndexRedirect />} />
          {/* Routes reserved for SUPER_ROOT users only */}
          <Route element={<ProtectedRoute requireSuperRoot={true} />}>
            <Route path="influxdb-test" element={<InfluxApiTestPage />} />
            <Route path="metric-catalog" element={<MetricCatalogPage />} />
            <Route path="smalt-core-ingest-sync" element={<SmaltCoreIngestSyncPage />} />
            <Route path="super-topology-debug" element={<SuperTopologyDebugPage />} />
            <Route path="super-zipato-analysis" element={<SuperZipatoAnalysisPage />} />
            <Route path="super-check-device-exist" element={<SuperCheckDeviceExistPage />} />
            <Route path="super-associations" element={<SuperAssociationsPage />} />
            <Route path="super-import-equipments" element={<SuperImportEquipmentsPage />} />
          </Route>
        </Route>
        {/* Routes for non-ROOT users to access their projects */}
        <Route path="/admin/projects/:projectId/areas" element={<AreasManagementPage />} />
        <Route path="/admin/projects/:projectId/areas/import" element={<ImportAreasPage />} />
        <Route element={<ProtectedRoute requireRootOrAdmin={true} />}>
          <Route path="/admin/projects/:projectId/association" element={<ProjectAssociationsPage />} />
        </Route>
        <Route path="/admin/projects/:projectId/association/import" element={<ImportBoxAssociationPage />} />
        <Route path="/admin/projects/:projectId/equipments" element={<EquipmentsManagementPage />} />
        <Route path="/admin/alarms" element={<AlarmsManagementPage />} />
          {/* Routes for ROOT or ADMIN: users management (admin cannot modify super admins) */}
          <Route element={<ProtectedRoute requireRootOrAdmin={true} />}>
            <Route path="/admin/users" element={<UsersManagementPage />} />
          </Route>
          {/* Admin UI (ROOT or ADMIN) */}
          <Route element={<ProtectedRoute requireRootOrAdmin={true} />}>
            <Route path="/admin/dashboard" element={<RootDashboardPage />} />
            <Route path="/admin/projects" element={<ProjectsPage />} />
            <Route path="/admin/projects/new" element={<AddProjectPage />} />
            <Route path="/admin/projects/:id/edit" element={<EditProjectPage />} />
            <Route path="/admin/projects/:projectId/alerts" element={<AlertsConfigPage />} />
            <Route path="/admin/projects/:projectId/members" element={<ProjectMembersPage />} />
            <Route path="/admin/projects/:projectId/box-accounts" element={<ProjectAccountsPage />} />
            <Route path="/admin/projects/:projectId/image" element={<ProjectImagePage />} />
            <Route path="/admin/projects/:projectId/analyses" element={<ProjectAnalysesPage />} />
            <Route path="/admin/projects/:projectId/analyses/consommation" element={<ProjectAnalysesConsommationPage />} />
            <Route path="/admin/projects/:projectId/analyses/puissance" element={<ProjectAnalysesPuissancePage />} />
            <Route path="/admin/projects/:projectId/analyses/confort" element={<ProjectAnalysesConfortPage />} />
            <Route path="/admin/projects/:projectId/analyses/comparaisons" element={<ProjectAnalysesComparaisonsPage />} />
            <Route path="/admin/projects/:projectId/analyses/rapports" element={<ProjectAnalysesRapportsPage />} />
            <Route path="/admin/projects/:projectId/analyses/logement" element={<ProjectAnalysesLogementPage />} />
            <Route path="/admin/projects/:projectId/configuration/attribute-data" element={<AttributeDataPage />} />
            <Route path="/admin/projects/:projectId/configuration/attribute-data/parties-communes" element={<AdminPartiesCommunesDevicesPage />} />
            <Route path="/admin/configuration/attribute-data" element={<AttributeDataPage />} />
            <Route path="/admin/configuration/attribute-data/parties-communes" element={<AdminPartiesCommunesDevicesPage />} />
        </Route>
        <Route element={<AppLayout />}>
          <Route path="/home" element={<HomePage />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default App;
